import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { fdate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";

// Move one pet or the whole group of a boarding booking to another kennel/room/suite.
// Server validates dates, capacity, same-customer sharing and "must board alone".
export function ReallocateButton({ bookingId, booking: initial, compact, onDone }) {
  const [open, setOpen] = useState(false);
  const [rid, setRid] = useState("");
  const [pets, setPets] = useState([]);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const id = bookingId || initial?.id;
  const { data: bk } = useQuery({ queryKey: ["booking", id], enabled: open, initialData: initial,
    queryFn: async () => (await api.get(`/bookings/${id}`)).data });
  const allocs = bk?.allocations || [];
  const ref = allocs[0];
  const moving = pets.length ? pets : allocs.flatMap((a) => a.pets.map((p) => p.id));
  const { data: avail } = useQuery({
    queryKey: ["realloc-avail", id, ref?.check_in, moving.join(",")], enabled: open && !!ref,
    queryFn: async () => (await api.get("/boarding/availability", { params: {
      start_time: ref.check_in, end_time: ref.expected_check_out, branch_id: bk.branch_id || "",
      customer_id: bk.customer_id, booking_id: id, pet_ids: moving.join(",") } })).data,
  });
  const current = new Set(allocs.map((a) => a.resource_id));
  const opts = (avail?.items || []).filter((r) => !current.has(r.id) || allocs.length > 1);
  const toggle = (pid) => setPets((s) => (s.includes(pid) ? s.filter((x) => x !== pid) : [...s, pid]));
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${id}/reallocate`, { resource_id: rid, pet_ids: pets, reason });
      toast.success(data.unchanged ? "Already in that room" : "Room changed. Charges unchanged.");
      ["bookings", "board-occ", "board-stays", "board-dash", "booking"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      setOpen(false); setRid(""); setPets([]); setReason(""); onDone?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid={`change-room-${compact ? "m-" : ""}${id}`}
        className="inline-flex items-center gap-1 rounded-md border border-sky-200 bg-sky-50 px-2 py-1 text-xs font-semibold text-sky-800 hover:bg-sky-100">
        <ArrowRightLeft className="h-3.5 w-3.5" /> Change room
      </button>
      {open && (
        <Dialog open onOpenChange={setOpen}>
          <DialogContent className="bg-white max-w-md" data-testid="change-room-dialog">
            <DialogHeader><DialogTitle className="font-display">Change room / kennel</DialogTitle></DialogHeader>
            <DialogDescription className="text-xs text-slate-500">
              {ref ? `${fdate(ref.check_in)} → ${fdate(ref.expected_check_out)}. ` : ""}Only units that can take the selected pets for the whole stay are listed.
            </DialogDescription>
            <div className="space-y-2" data-testid="change-room-current">
              {allocs.map((a) => (
                <div key={a.stay_id} className="rounded-lg border border-slate-200 p-2 text-xs">
                  <div className="font-semibold text-slate-700">Now in {a.resource_name || "—"} <span className="font-normal text-slate-400">({a.status.replace("_", " ")})</span></div>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {a.pets.map((p) => (
                      <label key={p.id} className="flex items-center gap-1">
                        <input type="checkbox" checked={pets.includes(p.id)} onChange={() => toggle(p.id)} data-testid={`change-room-pet-${p.id}`} /> {p.name || "Pet"}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
              <p className="text-[11px] text-slate-500">{pets.length ? `Moving ${pets.length} selected pet(s).` : "No pet ticked = move all pets together."}</p>
            </div>
            <select data-testid="change-room-select" value={rid} onChange={(e) => setRid(e.target.value)} aria-label="Destination"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white">
              <option value="">Select a destination…</option>
              {opts.map((r) => <option key={r.id} value={r.id} disabled={!r.available}>
                {r.name} · {r.occupied}/{r.capacity} pets{r.available ? "" : " — not available"}</option>)}
            </select>
            {rid && opts.find((r) => r.id === rid)?.reason && <p className="text-xs text-rose-600">{opts.find((r) => r.id === rid).reason}</p>}
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Reason for the change (required)"
              data-testid="change-room-reason" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <p className="text-[11px] text-slate-500" data-testid="change-room-price-note">Price stays the same: boarding is charged per pet per night, not per room.</p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} disabled={!rid || !reason.trim() || busy || !opts.find((r) => r.id === rid)?.available} data-testid="change-room-save" className="bg-slate-900 text-white">{busy ? "Moving…" : "Confirm move"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
